import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Gavel, Timer, ArrowRight } from "lucide-react";

function fmtCountdown(ends_at) {
  if (!ends_at) return "";
  const end = new Date(ends_at);
  const diff = end - new Date();
  if (diff <= 0) return "Ended";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return [d ? `${d}d` : "", h ? `${h}h` : "", `${m}m`].filter(Boolean).join(" ");
}

export default function AuctionDetailPage() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [bidder, setBidder] = useState({ bidder_name: "", bidder_email: "" });
  const [amount, setAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => { const { data } = await api.get(`/auctions/${id}`); setA(data); setAmount(Math.ceil((data.current_bid + (data.bid_increment || 250)) / 100) * 100); };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [id]);

  const placeBid = async (e) => {
    e.preventDefault();
    if (!bidder.bidder_name || !bidder.bidder_email) { toast.error("Enter your name and email to bid"); return; }
    setSubmitting(true);
    try {
      await api.post(`/auctions/${id}/bid`, { ...bidder, amount: Number(amount) });
      toast.success("Bid placed!");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (!a) return <PublicShell><div className="pt-32 max-w-7xl mx-auto px-6 text-slate-400">Loading…</div></PublicShell>;
  const v = a.vehicle || {};
  const minNext = a.current_bid + (a.bid_increment || 250);

  return (
    <PublicShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-12">
        <Link to="/auctions" className="text-sm text-slate-400 hover:text-white mb-6 inline-block">← Back to auctions</Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-4">
          <div>
            <div className="aspect-[16/10] rounded-md surface overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url(${v.images?.[0] || ""})` }} />
            <div className="surface p-6 rounded-md mt-4">
              <div className="label-eyebrow mb-3">Auction description</div>
              <p className="text-sm text-slate-300 leading-relaxed">{a.description}</p>
              <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-white/5">
                <div><div className="label-eyebrow mb-1">Starts</div><div className="text-xs text-slate-300">{new Date(a.starts_at).toLocaleString()}</div></div>
                <div><div className="label-eyebrow mb-1">Ends</div><div className="text-xs text-slate-300">{new Date(a.ends_at).toLocaleString()}</div></div>
                <div><div className="label-eyebrow mb-1">Increment</div><div className="text-xs text-slate-300">${a.bid_increment?.toLocaleString()}</div></div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-3"><span className="pill pill-sold">{a.status}</span><span className="text-xs font-mono text-[#f0c659] flex items-center gap-1"><Timer className="w-3 h-3" /> {fmtCountdown(a.ends_at)}</span></div>
            <h1 className="font-heading text-4xl lg:text-5xl font-light mb-2 chrome-text inline-block">{v.make}</h1>
            <h2 className="font-heading text-2xl text-slate-300 mb-6">{v.model}</h2>
            <div className="mb-6">
              <span className="chrome-nameplate"><span className="dot" />{v.year} · {v.body_type}<span className="dot" /></span>
            </div>

            <div className="surface p-6 rounded-md mb-6">
              <div className="label-eyebrow mb-1">Current bid</div>
              <div className="font-display text-6xl gold-text mb-2">${a.current_bid?.toLocaleString()}</div>
              <div className="text-sm text-slate-400">{a.bid_count || 0} bid{a.bid_count === 1 ? "" : "s"} · Min next bid ${minNext.toLocaleString()}</div>
              {a.high_bidder && <div className="text-xs text-slate-500 mt-2">High bidder: {a.high_bidder}</div>}
            </div>

            <form onSubmit={placeBid} className="surface p-6 rounded-md space-y-3" data-testid="bid-form">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-sm">Your name *</Label><Input required value={bidder.bidder_name} onChange={(e) => setBidder({ ...bidder, bidder_name: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="bid-name-input" /></div>
                <div><Label className="text-sm">Email *</Label><Input required type="email" value={bidder.bidder_email} onChange={(e) => setBidder({ ...bidder, bidder_email: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="bid-email-input" /></div>
                <div className="md:col-span-2"><Label className="text-sm">Bid amount *</Label><Input required type="number" min={minNext} step={a.bid_increment} value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="bg-[#04060d] border-white/10 h-12 mt-1 font-heading text-lg" data-testid="bid-amount-input" /></div>
              </div>
              <Button type="submit" disabled={submitting || a.status === "ended"} className="btn-cta rounded-sm h-12 w-full" data-testid="bid-submit-btn">
                <Gavel className="w-4 h-4 mr-2" />
                {submitting ? "Placing…" : `Place bid of $${Number(amount).toLocaleString()}`}
              </Button>
              <p className="text-xs text-slate-500">Bidding is binding. Winning bidder must complete purchase within 48 hours.</p>
            </form>
          </div>
        </div>

        {a.bids?.length > 0 && (
          <div className="surface rounded-md mt-8 overflow-hidden">
            <div className="px-5 py-3 surface-elev label-eyebrow">Bid history</div>
            <table className="w-full text-sm">
              <tbody>
                {a.bids.map((b) => (
                  <tr key={b.id} className="border-t border-white/5">
                    <td className="px-5 py-3">{b.bidder_name}</td>
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{new Date(b.placed_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right gold-text font-semibold">${b.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
